import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const SESSION_KEY = "ridgehawk_chat_session_id";
const HISTORY_KEY = "ridgehawk_chat_history";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function loadSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newSessionId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return newSessionId();
  }
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(messages) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-30)));
  } catch {
    /* ignore quota errors */
  }
}

const WELCOME = {
  role: "assistant",
  content:
    "Hi — I'm the Ridgehawk virtual assistant. I can answer questions about our **services**, **coverage areas**, and **certifications**, or help you start a quote. How can I help today?",
};

export default function ChatWidget() {
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/admin");

  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState(() => {
    const stored = loadHistory();
    return stored.length ? stored : [WELCOME];
  });
  // Counts user-sent messages within this widget mount — used for analytics.
  const userTurnsRef = useRef(0);
  const leadFiredRef = useRef(false);

  const sessionId = useMemo(() => loadSessionId(), []);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, sending]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  if (onAdmin) return null;

  async function send(text) {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    setError(null);
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setSending(true);
    userTurnsRef.current += 1;
    trackEvent("chat_message_sent", { message_index: userTurnsRef.current });
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      let data = {};
      try { data = await res.json(); } catch { data = {}; }
      if (!res.ok) {
        const detail = data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : detail?.message || `Request failed (${res.status}).`;
        setError(msg);
        return;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data?.lead_created && !leadFiredRef.current) {
        leadFiredRef.current = true;
        trackEvent("chat_lead_created", { lead_id: data.lead_id || null });
        trackEvent("generate_lead", { lead_type: "chat", value: 0 });
      }
    } catch (_) {
      setError("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  }

  function reset() {
    setMessages([WELCOME]);
    setError(null);
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
  }

  return (
    <>
      {!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); trackEvent("chat_open"); }}
          aria-label="Open chat assistant"
          data-testid="chat-fab"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-ridge-amber px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-[0_18px_36px_-12px_rgba(232,122,30,0.55)] transition-transform duration-200 hover:bg-ridge-amber-dark hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ridge-amber focus-visible:ring-offset-2 md:bottom-7 md:right-7"
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          Chat with us
        </button>
      ) : null}

      {open ? (
        <div
          role="dialog"
          aria-label="Ridgehawk virtual assistant"
          data-testid="chat-panel"
          className="fixed inset-x-3 bottom-3 top-auto z-40 flex max-h-[80vh] flex-col overflow-hidden rounded-lg border border-ridge-border bg-white shadow-[0_28px_56px_-16px_rgba(28,28,28,0.45)] sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[560px] sm:max-h-[80vh] sm:w-[380px] md:bottom-7 md:right-7"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-ridge-border bg-ridge-charcoal px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white p-0.5">
                <img src="/logo-icon.png" alt="" aria-hidden="true" className="h-full w-full object-contain" />
              </span>
              <div>
                <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Ridgehawk Assistant</p>
                <p className="flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.15em] text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-ridge-amber" aria-hidden="true" />
                  Online
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={reset}
                className="rounded px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
                data-testid="chat-reset"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="inline-flex h-8 w-8 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                data-testid="chat-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-ridge-stone/50 px-4 py-4"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {sending ? (
              <div className="flex items-center gap-2 text-xs text-ridge-muted" data-testid="chat-typing">
                <Loader2 className="h-3 w-3 animate-spin" />
                Assistant is typing…
              </div>
            ) : null}
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
                data-testid="chat-error"
              >
                {error}
              </div>
            ) : null}
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-1.5 border-t border-ridge-border bg-white px-3 py-2">
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              className="inline-flex items-center rounded-full border border-ridge-amber bg-white px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-amber transition-colors hover:bg-ridge-amber hover:text-white"
              data-testid="chat-quick-quote"
            >
              Get a quote
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center rounded-full border border-ridge-border bg-white px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-charcoal transition-colors hover:border-ridge-charcoal hover:bg-ridge-charcoal hover:text-white"
              data-testid="chat-quick-human"
            >
              Talk to a human
            </Link>
            <button
              type="button"
              onClick={() => send("What services do you offer?")}
              className="inline-flex items-center rounded-full border border-ridge-border bg-white px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-charcoal transition-colors hover:border-ridge-charcoal"
              data-testid="chat-quick-services"
            >
              Services
            </button>
            <button
              type="button"
              onClick={() => send("Where do you deliver?")}
              className="inline-flex items-center rounded-full border border-ridge-border bg-white px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-charcoal transition-colors hover:border-ridge-charcoal"
              data-testid="chat-quick-areas"
            >
              Coverage
            </button>
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-end gap-2 border-t border-ridge-border bg-white px-3 py-3"
            data-testid="chat-form"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder="Ask about pricing, coverage, or services…"
              className="block max-h-24 min-h-[40px] flex-1 resize-none rounded-md border border-ridge-border bg-white px-3 py-2 text-sm text-ridge-text placeholder:text-ridge-muted focus:border-ridge-amber focus:outline-none focus:ring-1 focus:ring-ridge-amber"
              maxLength={4000}
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              data-testid="chat-send"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white transition-colors hover:bg-ridge-amber disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      ) : null}
    </>
  );
}

function Bubble({ role, content }) {
  const mine = role === "user";
  return (
    <div
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
      data-testid={mine ? "chat-msg-user" : "chat-msg-assistant"}
    >
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
          mine
            ? "bg-ridge-charcoal text-white"
            : "border border-ridge-border bg-white text-ridge-text"
        }`}
      >
        {mine ? (
          <p className="whitespace-pre-wrap break-words">{content}</p>
        ) : (
          <div className="prose-ridge prose-ridge-chat break-words">
            <ReactMarkdown
              components={{
                a: ({ href, children, ...rest }) => {
                  const isInternal = typeof href === "string" && href.startsWith("/");
                  if (isInternal) {
                    return (
                      <Link to={href} {...rest}>
                        {children}
                      </Link>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noreferrer" {...rest}>
                      {children}
                    </a>
                  );
                },
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
